import { FC, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Search, Trash2, Check } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { NumericFormat } from 'react-number-format';
import type { ContainerType } from 'src/trpc';

export interface PayAsYouGoContainerRate {
  dailyRateInCents: number;
  perTonRateInCents: number;
}

interface ContainerPayAsYouGoMatrixProps {
  containerRates: Record<string, PayAsYouGoContainerRate>;
  onContainerRatesChange: (data: Record<string, PayAsYouGoContainerRate>) => void;
  containers: ContainerType[];
}

export const ContainerPayAsYouGoMatrix: FC<ContainerPayAsYouGoMatrixProps> = ({
  containerRates,
  onContainerRatesChange,
  containers,
}) => {
  const [selectedContainers, setSelectedContainers] = useState(Object.keys(containerRates));

  const addContainer = (containerId: string) => {
    if (!selectedContainers.includes(containerId)) {
      setSelectedContainers([...selectedContainers, containerId]);
      const newData = { ...containerRates };
      if (!newData[containerId]) {
        newData[containerId] = {
          dailyRateInCents: 0,
          perTonRateInCents: 0,
        };
      }
      onContainerRatesChange(newData);
    }
  };

  const removeContainer = (containerId: string) => {
    setSelectedContainers(selectedContainers.filter((id) => id !== containerId));
    const newData = { ...containerRates };
    delete newData[containerId];
    onContainerRatesChange(newData);
  };

  const updateContainerRate = (
    containerId: string,
    field: keyof PayAsYouGoContainerRate,
    value: number,
  ) => {
    const newData = { ...containerRates };
    if (newData[containerId]) {
      newData[containerId] = {
        ...newData[containerId],
        [field]: value,
      };
      onContainerRatesChange(newData);
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg">
        <div className="font-medium mb-1">Pay As You Go Pricing</div>
        <div>
          Customers pay a daily rate for container rental plus a per-ton rate for actual weight
          disposed. The total cost = (daily rate × number of days × container quantity) + (per ton
          rate × tonnage).
        </div>
      </div>

      <div>
        <Label>Select Containers</Label>
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              className="w-full justify-start bg-white hover:bg-gray-50 text-muted"
            >
              <Search className="mr-2 h-4 w-4 text-muted" />
              Add containers for pay as you go pricing
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search containers..." />
              <CommandList>
                <CommandEmpty>No containers found.</CommandEmpty>
                <CommandGroup>
                  {containers
                    .filter((container) => !selectedContainers.includes(container._id!))
                    .map((container) => (
                      <CommandItem
                        key={container._id!}
                        value={container.name}
                        onSelect={() => addContainer(container._id!)}
                        className="flex items-center hover:bg-gray-100 cursor-pointer"
                      >
                        <Check className="mr-2 h-4 w-4 text-primary opacity-0" />
                        <span className="flex-1">{container.name}</span>
                      </CommandItem>
                    ))}
                </CommandGroup>
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>

      {selectedContainers.length > 0 && (
        <div className="space-y-4">
          {selectedContainers.map((containerId) => {
            const container = containers.find((c) => c._id === containerId);
            const rate = containerRates[containerId];

            // Skip rendering if rate data is not available
            if (!rate) {
              return null;
            }

            return (
              <Card key={containerId}>
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">
                      {container?.name || 'Unknown Container'}
                    </CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeContainer(containerId)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor={`dailyRate-${containerId}`} className="text-sm font-medium">
                        Daily Rate
                      </Label>
                      <NumericFormat
                        customInput={Input}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale={true}
                        id={`dailyRate-${containerId}`}
                        value={rate.dailyRateInCents / 100}
                        onValueChange={(values) => {
                          const cents = Math.round((values.floatValue || 0) * 100);
                          updateContainerRate(containerId, 'dailyRateInCents', cents);
                        }}
                        placeholder="0.00"
                        className="mt-1.5"
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        Daily rental rate per container
                      </p>
                    </div>

                    <div>
                      <Label htmlFor={`perTonRate-${containerId}`} className="text-sm font-medium">
                        Per Ton Rate
                      </Label>
                      <NumericFormat
                        customInput={Input}
                        prefix="$"
                        decimalScale={2}
                        fixedDecimalScale={true}
                        id={`perTonRate-${containerId}`}
                        value={rate.perTonRateInCents / 100}
                        onValueChange={(values) => {
                          const cents = Math.round((values.floatValue || 0) * 100);
                          updateContainerRate(containerId, 'perTonRateInCents', cents);
                        }}
                        placeholder="0.00"
                        className="mt-1.5"
                      />
                      <p className="text-xs text-gray-500 mt-1.5">
                        Rate charged per ton of weight
                      </p>
                    </div>
                  </div>

                  {rate.dailyRateInCents > 0 && rate.perTonRateInCents > 0 && (
                    <div className="text-xs text-gray-600 bg-gray-50 p-2 rounded">
                      <strong>Example:</strong> For a 10-day rental with 2 tons of waste:{' '}
                      <span className="font-medium">
                        ${((rate.dailyRateInCents * 10) / 100).toFixed(2)}
                      </span>{' '}
                      (rental) +{' '}
                      <span className="font-medium">
                        ${((rate.perTonRateInCents * 2) / 100).toFixed(2)}
                      </span>{' '}
                      (disposal) ={' '}
                      <span className="font-medium">
                        ${((rate.dailyRateInCents * 10 + rate.perTonRateInCents * 2) / 100).toFixed(2)}
                      </span>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {selectedContainers.length === 0 && (
        <div className="text-sm text-gray-500 text-center py-8 border border-dashed rounded-lg">
          No containers selected. Click the button above to add containers.
        </div>
      )}
    </div>
  );
};
